import React, { useCallback, useEffect, useState } from "react";
import HelpBubble from "./HelpBubble";
import { RULES_HELP } from "./helpTexts";
import { bulkPriceRules, createPriceRule, getPriceRules, patchPriceRule } from "../../../api/marketplacePricingApi";
import BrandAutocomplete from "./BrandAutocomplete";
import parseRuleLines from "./parseRuleLines";
import { RULE_MODE_LABELS, RULE_MODE_UNITS, RULE_STATUS_LABELS, formatApiError, money } from "./pricingFormat";

const STATUS_PILL = {
    active: "mp-pill mp-pill-good",
    scheduled: "mp-pill mp-pill-accent",
    expired: "mp-pill",
    inactive: "mp-pill",
};

export const emptyRuleDraft = () => ({
    scope_type: "brand",
    scope_value: "",
    supplier_code: "",
    mode: "rrp_discount_percent",
    value: "",
    valid_from: "",
    valid_until: "",
    allow_below_breakeven: false,
    reason: "",
});

const toIso = (value) => (value ? new Date(value).toISOString() : null);

export const buildRulePayload = (draft) => {
    const scopeValue = draft.scope_value.trim();
    if (!scopeValue) throw new Error(draft.scope_type === "brand" ? "Выберите бренд" : "Укажите SKU");
    const value = Number(String(draft.value).replace(",", "."));
    if (String(draft.value).trim() === "" || !Number.isFinite(value) || value < 0) {
        throw new Error("Значение должно быть неотрицательным числом");
    }
    if (RULE_MODE_UNITS[draft.mode] === "%" && value > 100) throw new Error("Процент не может быть больше 100");
    if (draft.mode === "fixed_price" && !draft.valid_until) throw new Error("Для фиксированной цены укажите дату окончания");
    return {
        scope_type: draft.scope_type,
        scope_value: scopeValue,
        supplier_code: draft.supplier_code.trim().toUpperCase() || null,
        mode: draft.mode,
        value,
        valid_from: toIso(draft.valid_from),
        valid_until: toIso(draft.valid_until),
        allow_below_breakeven: Boolean(draft.allow_below_breakeven),
        reason: draft.reason.trim() || null,
    };
};

const formatPeriod = (rule) => {
    const from = rule.valid_from ? new Date(rule.valid_from).toLocaleDateString("uk-UA") : null;
    const until = rule.valid_until ? new Date(rule.valid_until).toLocaleDateString("uk-UA") : null;
    if (!from && !until) return "бессрочно";
    return [from ? `с ${from}` : null, until ? `до ${until}` : null].filter(Boolean).join(" ");
};

export default function PriceRulesPanel({ platform, accountId }) {
    const [items, setItems] = useState([]);
    const [total, setTotal] = useState(0);
    const [statusFilter, setStatusFilter] = useState("active");
    const [query, setQuery] = useState("");
    const [draft, setDraft] = useState(emptyRuleDraft);
    const [bulkText, setBulkText] = useState("");
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState("");
    const [success, setSuccess] = useState("");

    const load = useCallback(async () => {
        if (!accountId) return;
        setLoading(true);
        try {
            const data = await getPriceRules(platform, accountId, { status: statusFilter, q: query.trim() || undefined });
            setItems(data.items || []);
            setTotal(data.total || 0);
        } catch (apiError) {
            setError(formatApiError(apiError, "Не удалось загрузить правила."));
        } finally {
            setLoading(false);
        }
    }, [platform, accountId, statusFilter, query]);

    useEffect(() => {
        load();
    }, [load]);

    const change = (key, value) => {
        setDraft((old) => ({ ...old, [key]: value }));
        setError("");
        setSuccess("");
    };

    const submit = async (event) => {
        event.preventDefault();
        let payload;
        try {
            payload = buildRulePayload(draft);
        } catch (validationError) {
            setError(validationError.message);
            return;
        }
        setSaving(true);
        try {
            const saved = await createPriceRule(platform, accountId, payload);
            setSuccess(`Правило сохранено: ${saved.scope_value}.`);
            setDraft((old) => ({ ...emptyRuleDraft(), scope_type: old.scope_type, mode: old.mode }));
            await load();
        } catch (apiError) {
            setError(formatApiError(apiError, "Не удалось сохранить правило."));
        } finally {
            setSaving(false);
        }
    };

    const submitBulk = async () => {
        const { items: parsed, errors } = parseRuleLines(bulkText, draft);
        if (!parsed.length) {
            setError(errors.length ? errors.slice(0, 10).join("; ") : "Нет строк для загрузки");
            return;
        }
        setSaving(true);
        setError("");
        setSuccess("");
        try {
            const result = await bulkPriceRules(platform, accountId, parsed);
            const allErrors = [...errors, ...(result.errors || [])];
            setSuccess(`Создано ${result.created}, обновлено ${result.updated}.`);
            if (allErrors.length) setError(`Пропущено: ${allErrors.slice(0, 10).join("; ")}${allErrors.length > 10 ? "…" : ""}`);
            setBulkText("");
            await load();
        } catch (apiError) {
            setError(formatApiError(apiError, "Не удалось загрузить правила."));
        } finally {
            setSaving(false);
        }
    };

    const deactivate = async (rule) => {
        setSaving(true);
        setError("");
        try {
            await patchPriceRule(platform, accountId, rule.id, { is_active: false });
            setSuccess(`Правило ${rule.scope_value} выключено.`);
            await load();
        } catch (apiError) {
            setError(formatApiError(apiError, "Не удалось выключить правило."));
        } finally {
            setSaving(false);
        }
    };

    const unit = RULE_MODE_UNITS[draft.mode];

    return (
        <section className="mp-card" aria-label="Правила цены">
            <HelpBubble title="Скидки и правила">{RULES_HELP}</HelpBubble>
            <div className="mp-card-head">
                <div>
                    <h3 className="mp-title">Скидки и правила на бренды и товары</h3>
                    <p className="mp-lead">
                        Работают в режиме «от безубыточности». Правило на товар сильнее правила на бренд. Если цена правила
                        ниже безубыточности, оно не применяется, пока не разрешено явно.
                    </p>
                </div>
            </div>

            <form className="mp-group" onSubmit={submit} aria-label="Новое правило">
                <h4 className="mp-group-title">Новое правило</h4>
                <div className="mp-radio-row" role="radiogroup" aria-label="Область правила">
                    {[["brand", "Бренд"], ["product", "Товар (SKU)"]].map(([scope, label]) => (
                        <label key={scope} className={draft.scope_type === scope ? "mp-radio selected" : "mp-radio"}>
                            <input
                                type="radio"
                                name="scope_type"
                                checked={draft.scope_type === scope}
                                onChange={() => {
                                    setDraft((old) => ({ ...old, scope_type: scope, scope_value: "" }));
                                    setError("");
                                    setSuccess("");
                                }}
                            />
                            {label}
                        </label>
                    ))}
                </div>
                <div className="mp-groups">
                    <label className="mp-field">
                        {draft.scope_type === "brand" ? "Бренд" : "SKU"}
                        {draft.scope_type === "brand" ? (
                            <BrandAutocomplete
                                platform={platform}
                                accountId={accountId}
                                value={draft.scope_value}
                                disabled={saving}
                                onChange={(value) => change("scope_value", value)}
                            />
                        ) : (
                            <input
                                className="mp-input"
                                aria-label="SKU"
                                value={draft.scope_value}
                                disabled={saving}
                                onChange={(event) => change("scope_value", event.target.value)}
                            />
                        )}
                    </label>
                    <label className="mp-field">
                        Тип правила
                        <select
                            className="mp-select"
                            aria-label="Тип правила"
                            value={draft.mode}
                            disabled={saving}
                            onChange={(event) => change("mode", event.target.value)}
                        >
                            {Object.entries(RULE_MODE_LABELS).map(([mode, label]) => (
                                <option key={mode} value={mode}>{label}</option>
                            ))}
                        </select>
                    </label>
                    <label className="mp-field">
                        Значение
                        <div className="mp-input-suffix">
                            <input
                                className="mp-input"
                                aria-label="Значение"
                                inputMode="decimal"
                                value={draft.value}
                                disabled={saving}
                                onChange={(event) => change("value", event.target.value)}
                            />
                            <span>{unit}</span>
                        </div>
                    </label>
                    <label className="mp-field">
                        Поставщик (необязательно)
                        <input
                            className="mp-input"
                            aria-label="Поставщик"
                            placeholder="например D8"
                            value={draft.supplier_code}
                            disabled={saving}
                            onChange={(event) => change("supplier_code", event.target.value)}
                        />
                    </label>
                    <label className="mp-field">
                        Действует с
                        <input
                            className="mp-input"
                            type="datetime-local"
                            aria-label="Действует с"
                            value={draft.valid_from}
                            disabled={saving}
                            onChange={(event) => change("valid_from", event.target.value)}
                        />
                    </label>
                    <label className="mp-field">
                        Действует до
                        <input
                            className="mp-input"
                            type="datetime-local"
                            aria-label="Действует до"
                            value={draft.valid_until}
                            disabled={saving}
                            onChange={(event) => change("valid_until", event.target.value)}
                        />
                    </label>
                    <label className="mp-field">
                        Причина
                        <input
                            className="mp-input"
                            aria-label="Причина"
                            value={draft.reason}
                            disabled={saving}
                            onChange={(event) => change("reason", event.target.value)}
                        />
                    </label>
                </div>
                <label className="mp-check">
                    <input
                        type="checkbox"
                        checked={draft.allow_below_breakeven}
                        disabled={saving}
                        onChange={(event) => change("allow_below_breakeven", event.target.checked)}
                    />
                    Разрешить цену ниже безубыточности (осознанная убыточная акция)
                </label>
                <div className="mp-actions">
                    <button type="submit" className="mp-btn mp-btn-primary" disabled={saving}>
                        {saving ? "Сохраняем…" : "Добавить правило"}
                    </button>
                </div>
                <details>
                    <summary>Загрузить списком</summary>
                    <p className="mp-muted">
                        Одна строка — одно правило: «{draft.scope_type === "brand" ? "бренд" : "SKU"}; значение; [до ГГГГ-ММ-ДД];
                        [поставщик]; [причина]». Тип правила и разрешение ниже безубыточности берутся из формы выше.
                    </p>
                    <textarea
                        className="mp-textarea"
                        aria-label="Правила списком"
                        value={bulkText}
                        disabled={saving}
                        onChange={(event) => setBulkText(event.target.value)}
                    />
                    <div className="mp-actions">
                        <button type="button" className="mp-btn" disabled={saving || !bulkText.trim()} onClick={submitBulk}>
                            Загрузить
                        </button>
                    </div>
                </details>
            </form>

            {error ? <div className="mp-callout mp-callout-error" role="alert">{error}</div> : null}
            {success ? <div className="mp-callout mp-callout-ok" role="status">{success}</div> : null}

            <div className="mp-actions">
                <select
                    className="mp-select"
                    style={{ width: "auto" }}
                    aria-label="Статус правил"
                    value={statusFilter}
                    onChange={(event) => setStatusFilter(event.target.value)}
                >
                    {["active", "scheduled", "expired", "inactive", "all"].map((status) => (
                        <option key={status} value={status}>{RULE_STATUS_LABELS[status] || "Все"}</option>
                    ))}
                </select>
                <input
                    className="mp-input"
                    style={{ maxWidth: 260 }}
                    aria-label="Поиск правил"
                    placeholder="Поиск по бренду, SKU, поставщику"
                    value={query}
                    onChange={(event) => setQuery(event.target.value)}
                />
                <span className="mp-muted">{loading ? "Загрузка…" : `Правил: ${total}`}</span>
            </div>

            <div className="mp-table-scroll">
                <table className="mp-table">
                    <thead>
                        <tr>
                            <th>Область</th>
                            <th>Поставщик</th>
                            <th>Правило</th>
                            <th className="mp-num">Значение</th>
                            <th>Период</th>
                            <th>Ниже безуб.</th>
                            <th>Статус</th>
                            <th>Причина</th>
                            <th />
                        </tr>
                    </thead>
                    <tbody>
                        {items.map((rule) => (
                            <tr key={rule.id}>
                                <td>
                                    <span className="mp-pill">{rule.scope_type === "brand" ? "Бренд" : "SKU"}</span>{" "}
                                    <strong>{rule.scope_value}</strong>
                                    <div className="mp-muted">
                                        {rule.scope_type === "brand"
                                            ? `${rule.brand_sku_count ?? 0} SKU в каталоге`
                                            : rule.product_name || ""}
                                    </div>
                                </td>
                                <td>{rule.supplier_code ? `${rule.supplier_code} ${rule.supplier_name || ""}` : "все"}</td>
                                <td>{RULE_MODE_LABELS[rule.mode] || rule.mode}</td>
                                <td className="mp-num">{money(rule.value)} {RULE_MODE_UNITS[rule.mode]}</td>
                                <td>{formatPeriod(rule)}</td>
                                <td>{rule.allow_below_breakeven ? "разрешено" : "—"}</td>
                                <td><span className={STATUS_PILL[rule.status] || "mp-pill"}>{RULE_STATUS_LABELS[rule.status] || rule.status}</span></td>
                                <td>{rule.reason || ""}</td>
                                <td>
                                    {rule.is_active ? (
                                        <button
                                            type="button"
                                            className="mp-btn mp-btn-small mp-btn-ghost"
                                            disabled={saving}
                                            onClick={() => deactivate(rule)}
                                        >
                                            Выключить
                                        </button>
                                    ) : null}
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
                {!items.length && !loading ? <p className="mp-muted">Правил нет.</p> : null}
            </div>
        </section>
    );
}
